
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DollarSign, Save, Loader2 } from "lucide-react";
import { useGlobalPricing } from "@/hooks/useGlobalPricing";

interface GlobalPricingTabProps {
  psychologistId?: string;
}

const GlobalPricingTab: React.FC<GlobalPricingTabProps> = ({ psychologistId }) => {
  const { pricing, isLoading, isSaving, savePricing } = useGlobalPricing(psychologistId);
  const [price30, setPrice30] = useState('150.00');
  const [price60, setPrice60] = useState('280.00');

  // Atualizar campos quando preços carregam
  useEffect(() => {
    setPrice30(pricing.price_30min.toFixed(2));
    setPrice60(pricing.price_60min.toFixed(2));
  }, [pricing]);

  const handleSave = async () => {
    const price30Number = parseFloat(price30);
    const price60Number = parseFloat(price60);

    if (isNaN(price30Number) || price30Number <= 0) {
      return;
    }

    if (isNaN(price60Number) || price60Number <= 0) {
      return;
    }

    await savePricing({
      price_30min: price30Number,
      price_60min: price60Number
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
            <p className="text-muted-foreground">Carregando preços...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-green-500" />
          Preços Globais (Sistema 24h)
        </CardTitle>
        <CardDescription>
          Configure seus preços para consultas de 30 e 60 minutos. Sistema pronto para expansão mundial! 🌍
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 30 minutos */}
          <div className="space-y-2">
            <Label htmlFor="price30" className="text-base font-medium">
              30 minutos
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                R$
              </span>
              <Input
                id="price30"
                type="number"
                step="0.01"
                min="0"
                value={price30}
                onChange={(e) => setPrice30(e.target.value)}
                className="pl-10 text-lg"
                placeholder="150.00"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Preço para consultas de 30 minutos
            </p>
          </div>

          {/* 60 minutos */}
          <div className="space-y-2">
            <Label htmlFor="price60" className="text-base font-medium">
              60 minutos
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                R$
              </span>
              <Input
                id="price60"
                type="number"
                step="0.01"
                min="0"
                value={price60}
                onChange={(e) => setPrice60(e.target.value)}
                className="pl-10 text-lg"
                placeholder="280.00"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Preço para consultas de 60 minutos
            </p>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2">Preview dos Preços:</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span>30min:</span>
              <span className="font-medium">R$ {parseFloat(price30 || '0').toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>60min:</span>
              <span className="font-medium">R$ {parseFloat(price60 || '0').toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Botão Salvar */}
        <Button 
          onClick={handleSave}
          disabled={isSaving}
          className="w-full"
          size="lg"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Salvar Preços
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default GlobalPricingTab;
